import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { CheckCircle2, XCircle, Phone, Loader2, Globe } from 'lucide-react'
import { generateSeo } from '@/lib/seo'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { ToolPageHero } from '@/components/ToolPageHero'
import { FAQSection } from '@/components/FAQSection'
import { CTASection } from '@/components/CTASection'
import { useState, useCallback } from 'react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { COUNTRIES_CONFIG } from '@/config/countries'
import { Flag } from '@/components/Flag'

export const Route = createFileRoute('/$lang/tools/phone-validator')({
  staticData: {
    sitemap: { priority: '0.7', changefreq: 'monthly' },
  },
  component: PhoneValidatorToolPage,
  head: ({ match }) => {
    const { meta, links } = generateSeo({
      title: 'Phone Number Validator - Check International Numbers | PhoneSalad',
      description: 'Validate any phone number and check its format, country, and type. Supports international numbers from 200+ countries.',
      pathname: match.pathname,
      keywords: 'phone number validator, validate phone number, international phone number check, phone number format',
    })
    return { meta, links }
  },
})

interface ValidationResult {
  isValid: boolean
  phoneNumber: ReturnType<typeof parsePhoneNumberFromString>
  countryName?: string
  countryCode?: string
}

const EXAMPLE_NUMBERS = [
  { label: 'uk', number: '+44 20 7123 4567' },
  { label: 'us', number: '+1 555-123-4567' },
  { label: 'germany', number: '+49 151 12345678' },
  { label: 'france', number: '+33 1 23 45 67 89' },
  { label: 'australia', number: '+61 412 345 678' },
  { label: 'india', number: '+91 98765 43210' },
]

function PhoneValidatorToolPage() {
  const { t } = useTranslation('tools')
  useLang()
  const [inputValue, setInputValue] = useState('')
  const [result, setResult] = useState<ValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  const validateNumber = useCallback(() => {
    if (!inputValue.trim()) {
      setResult({ isValid: false, phoneNumber: undefined })
      return
    }

    setIsValidating(true)

    // Simulate a brief delay for better UX
    setTimeout(() => {
      const parsed = parsePhoneNumberFromString(inputValue)

      if (parsed) {
        const countryCode = parsed.country as string
        const countryConfig = COUNTRIES_CONFIG[countryCode]

        setResult({
          isValid: parsed.isValid(),
          phoneNumber: parsed,
          countryName: countryCode ? t(`countries.${countryCode}`) : undefined,
          countryCode: countryCode || undefined,
        })
      } else {
        setResult({
          isValid: false,
          phoneNumber: undefined,
        })
      }

      setIsValidating(false)
    }, 300)
  }, [inputValue])

  const handleExampleClick = useCallback((number: string) => {
    setInputValue(number)
    setResult(null)
  }, [])

  const getNumberTypeLabel = (type: string | undefined) => {
    if (!type) return t('tools.phoneValidator.result.types.UNKNOWN')
    return t(`tools.phoneValidator.result.types.${type}`, { defaultValue: type })
  }

  const faqItems = t('tools.phoneValidator.faq.items', { returnObjects: true }) as unknown as Array<{ question: string; answer: string }>

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('tools.breadcrumb'),
            to: '/$lang/tools',
          },
          {
            label: t('tools.phoneValidator.title'),
          },
        ]}
      />

      <ToolPageHero
        icon={Phone}
        badgeText={t('tools.phoneValidator.title')}
        title={t('tools.phoneValidator.title')}
        description={t('tools.phoneValidator.description')}
      />

      {/* Input Card */}
      <section className="pb-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('tools.phoneValidator.inputLabel')}
                  </label>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder={t('tools.phoneValidator.inputPlaceholder')}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && validateNumber()}
                      className="flex-1 py-6 text-lg"
                    />
                    <Button
                      size="lg"
                      onClick={validateNumber}
                      disabled={isValidating || !inputValue.trim()}
                    >
                      {isValidating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('tools.phoneValidator.validating')}
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {t('tools.phoneValidator.validateButton')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Examples */}
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('tools.phoneValidator.examples.title')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLE_NUMBERS.map((example) => (
                      <button
                        key={example.label}
                        onClick={() => handleExampleClick(example.number)}
                        className="text-xs px-3 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                      >
                        {t(`tools.phoneValidator.examples.${example.label}` as any)}: {example.number}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <Card className={result.isValid ? 'border-green-500/50' : 'border-red-500/50'}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  {result.isValid ? (
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <div>
                    <CardTitle className="text-xl">
                      {result.isValid
                        ? t('tools.phoneValidator.result.valid')
                        : t('tools.phoneValidator.result.invalid')
                      }
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>

              {result.isValid && result.phoneNumber && (
                <CardContent className="space-y-6">
                  {/* Country Info */}
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    {result.countryCode && (
                      <Flag countryCode={result.countryCode} size="xl" />
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('tools.phoneValidator.result.country')}
                      </p>
                      <p className="text-xl font-semibold">
                        {result.countryName || result.phoneNumber.country}
                      </p>
                    </div>
                  </div>

                  {/* Number Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('tools.phoneValidator.result.numberType')}
                      </p>
                      <Badge variant="secondary" className="text-base">
                        {getNumberTypeLabel(result.phoneNumber.getType())}
                      </Badge>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('tools.phoneValidator.result.countryCode')}
                      </p>
                      <p className="text-base font-mono">
                        +{result.phoneNumber.countryCallingCode}
                      </p>
                    </div>
                  </div>

                  {/* Formats */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      {t('tools.phoneValidator.result.formats.title')}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm text-muted-foreground">
                          {t('tools.phoneValidator.result.formats.international')}
                        </span>
                        <span className="font-mono font-medium">
                          {result.phoneNumber.formatInternational()}
                        </span>
                      </div>
                      <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm text-muted-foreground">
                          {t('tools.phoneValidator.result.formats.national')}
                        </span>
                        <span className="font-mono font-medium">
                          {result.phoneNumber.formatNational()}
                        </span>
                      </div>
                      <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm text-muted-foreground">
                          {t('tools.phoneValidator.result.formats.e164')}
                        </span>
                        <span className="font-mono font-medium">
                          {result.phoneNumber.format('E.164')}
                        </span>
                      </div>
                      <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm text-muted-foreground">
                          {t('tools.phoneValidator.result.formats.rfc3966')}
                        </span>
                        <span className="font-mono font-medium text-sm">
                          {result.phoneNumber.format('RFC3966')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="pt-4 border-t">
                    <Button size="lg" className="w-full" asChild>
                      <a href={`/dialer?phone=${encodeURIComponent(result.phoneNumber.format('E.164'))}`}>
                        <Phone className="w-5 h-5 mr-2" />
                        {t('tools.phoneValidator.cta.button')}
                      </a>
                    </Button>
                  </div>
                </CardContent>
              )}

              {!result.isValid && (
                <CardContent>
                  <p className="text-muted-foreground">
                    {t('tools.phoneValidator.errors.invalid')}
                  </p>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      </section>

      <FAQSection
        title={t('tools.phoneValidator.faq.title')}
        items={faqItems}
      />

      <CTASection
        translationKeyRoot="tools.phoneValidator.cta" ns="tools"
        buttonTo="/dialer"
        buttonIcon={Globe}
      />
    </>
  )
}
